import styled, { css } from 'styled-components'

import { Spacings } from '@/theme/defaults'

import { Label } from '../label'
import { INPUT_FONT_SIZE_MAP, INPUT_LINE_HEIGHT_MAP } from '../text-input/text-input.styled'
import { SwitchSize } from './switch.types'

const ROW_PADDING_MAP: Record<SwitchSize, keyof Spacings> = {
  large: 24,
  medium: 20,
  small: 16,
}

export const Footer = styled.div<{ $size: SwitchSize }>(({ theme, $size }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  fontSize: INPUT_FONT_SIZE_MAP[$size],
  gap: theme.space['8'],
  lineHeight: INPUT_LINE_HEIGHT_MAP[$size],
  minHeight: INPUT_LINE_HEIGHT_MAP[$size],
}))

export const Message = styled.span<{ $hasError: boolean }>(({ theme, $hasError }) => ({
  color: $hasError ? theme.colors.red500 : theme.colors.primary600,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const SIZES = {
  large: {
    height: '2.4rem',
    thumbOffset: '0.2rem',
    thumbSize: '2rem',
    thumbTranslate: '2.6rem',
    width: '5rem',
  },
  medium: {
    height: '2rem',
    thumbOffset: '0.2rem',
    thumbSize: '1.6rem',
    thumbTranslate: '1.6rem',
    width: '3.6rem',
  },
  small: {
    height: '1.6rem',
    thumbOffset: '0.2rem',
    thumbSize: '1.2rem',
    thumbTranslate: '1.4rem',
    width: '3rem',
  },
}

export const Container = styled.div<{
  $size: SwitchSize
  $hasLabel: boolean
  $shouldShowFooter: boolean
}>(({ theme, $size, $hasLabel, $shouldShowFooter }) => {
  const rowPadding = theme.space[ROW_PADDING_MAP[$size]]

  return {
    alignItems: $hasLabel ? 'center' : 'flex-start',
    display: 'flex',
    flexDirection: $hasLabel ? 'row' : 'column',
    gap: theme.space['8'],
    justifyContent: $hasLabel ? 'space-between' : 'flex-start',
    paddingBottom: $hasLabel ? ($shouldShowFooter ? 0 : rowPadding) : 0,
    paddingTop: $hasLabel ? rowPadding : 0,
    width: $hasLabel ? '100%' : 'fit-content',
  }
})

// Note: label + message stacked in the left column, `flex: 1` so it takes all
// available width and pushes `SwitchControl` to the far right.
export const LabelColumn = styled.div(() => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
}))

// Note: holds the track/thumb control and the optional `inlineLabel` side by side.
export const SwitchControl = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  gap: theme.space['8'],
}))

export const InlineLabel = styled(Label)({
  fontSize: '1.2rem',
  minWidth: '2.1rem',
})

export const SwitchInput = styled.input({
  height: 0,
  opacity: 0,
  pointerEvents: 'none',
  position: 'absolute',
  width: 0,
})

export const SwitchLabel = styled.label<{
  $disabled: boolean
  $readOnly: boolean
  $checked: boolean
}>(({ theme, $disabled, $readOnly, $checked }) => {
  const staticBorderColor = $disabled && !$checked ? theme.colors.gray500 : 'transparent'

  return css`
      align-items: center;
      border: ${theme.border.size['1']} solid ${staticBorderColor};
      border-radius: ${theme.border.radius['full']};
      cursor: ${$disabled ? 'not-allowed' : $readOnly ? 'default' : 'pointer'};
      display: inline-flex;
      position: relative;
      transition: border-color ${theme.transition.duration.sm};

      ${
        !$disabled &&
        !$readOnly &&
        css`
        &:active,
        &[data-active='true'],
        &:has(${SwitchInput}:focus-visible) {
          border-color: ${theme.colors.gray200};
        }
      `
      }
    `
})

export const SwitchTrack = styled.div<{
  $size: SwitchSize
  $checked: boolean
  $disabled: boolean
  // Note: false for disabled and read-only switches - neither gets hover / pressed feedback
  $interactive: boolean
}>(({ theme, $size, $checked, $disabled, $interactive }) => {
  const size = SIZES[$size]

  const backgroundColor = $checked
    ? $disabled
      ? theme.colors.primary100
      : theme.colors.black800
    : $disabled
      ? theme.colors.gray300
      : theme.colors.gray500

  return css`
    background: ${backgroundColor};
    border-radius: ${theme.border.radius['full']};
    display: inline-block;
    height: ${size.height};
    position: relative;
    transition: background-color ${theme.transition.duration.sm};
    width: ${size.width};

    ${
      $interactive &&
      css`
      &:hover {
        background: ${$checked ? theme.colors.primary700 : theme.colors.gray700};
      }

      ${SwitchInput}:focus-visible + &,
      &:active,
      &[data-active='true'] {
        background: ${$checked ? theme.colors.black : theme.colors.gray500};
      }
    `
    }
  `
})

export const SwitchThumb = styled.div<{
  $size: SwitchSize
  $checked: boolean
  $disabled: boolean
}>(({ theme, $size, $checked }) => {
  const size = SIZES[$size]

  return {
    backgroundColor: theme.colors.white,
    borderRadius: theme.border.radius['circle'],
    boxShadow: theme.shadow.sm,
    height: size.thumbSize,
    left: size.thumbOffset,
    position: 'absolute',
    top: size.thumbOffset,
    transform: $checked ? `translateX(${size.thumbTranslate})` : 'translateX(0)',
    transition: `transform ${theme.transition.duration.sm}`,
    width: size.thumbSize,
  }
})
